import React, { useEffect, useState } from 'react';
import {
    Card,
    Row,
    Col,
    Table,
    Button,
    Modal,
    Form,
    Input,
    Select,
    Upload,
    message,
    Space,
    Tag,
    Tooltip,
    Statistic,
    Alert,
    Typography,
    DatePicker,
    Checkbox,
    Divider,
} from 'antd';
import {
    UploadOutlined,
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    CreditCardOutlined,
    FileExcelOutlined,
    CheckOutlined,
    CloseOutlined,
    ProjectOutlined,
    BankOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// 예산 카테고리명 변환 함수
const getBudgetCategoryName = (category) => {
    const categoryNames = {
        outsourcing: '외주비',
        material: '자재비',
        expense: '경비',
        other: '기타'
    };
    return categoryNames[category] || category;
};

const CorporateCardPage = () => {
    const [loading, setLoading] = useState(false);
    const [cards, setCards] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [projects, setProjects] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [users, setUsers] = useState([]);
    const [stats, setStats] = useState({});
    const [currentUser, setCurrentUser] = useState(null);
    
    // Modal states
    const [cardModalVisible, setCardModalVisible] = useState(false);
    const [uploadModalVisible, setUploadModalVisible] = useState(false);
    const [mappingModalVisible, setMappingModalVisible] = useState(false);
    const [singleMappingModalVisible, setSingleMappingModalVisible] = useState(false);
    const [editingCard, setEditingCard] = useState(null);
    const [selectedCard, setSelectedCard] = useState(null);
    const [selectedCardInfo, setSelectedCardInfo] = useState(null);
    const [uploadLoading, setUploadLoading] = useState(false);
    const [selectedTransactions, setSelectedTransactions] = useState([]);
    const [currentTransaction, setCurrentTransaction] = useState(null);
    const [projectBudgetItems, setProjectBudgetItems] = useState([]);
    const [selectedProjectId, setSelectedProjectId] = useState(null);
    const [selectedBudgetCategory, setSelectedBudgetCategory] = useState(null);
    
    // Form
    const [cardForm] = Form.useForm();
    const [uploadForm] = Form.useForm();
    const [mappingForm] = Form.useForm();
    const [singleMappingForm] = Form.useForm();
    
    // Filters
    const [filters, setFilters] = useState({
        dateRange: [dayjs().startOf('month'), dayjs().endOf('month')],
        cardId: null,
        mappingStatus: null
    });

    useEffect(() => {
        fetchCurrentUser();
    }, []);

    useEffect(() => {
        if (currentUser) {
            fetchInitialData();
        }
    }, [currentUser]);

    useEffect(() => {
        fetchTransactions();
        fetchStats();
    }, [filters]);

    // 인증 에러 처리 공통 함수
    const handleAuthError = (error) => {
        if (error.response?.status === 401) {
            message.error('인증이 만료되었습니다. 다시 로그인해주세요');
            localStorage.removeItem('token');
            window.location.href = '/login';
            return true;
        }
        return false;
    };

    // 현재 사용자 정보 가져오기
    const fetchCurrentUser = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_BASE}/auth/me`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setCurrentUser(response.data.user);
        } catch (error) {
            console.error('Current user fetch error:', error);
            handleAuthError(error);
        }
    };

    // 권한 체크 함수
    const hasAdminOrFinanceRole = () => {
        if (!currentUser) return false;
        
        console.log('🔍 currentUser:', currentUser);
        console.log('🔍 currentUser.roles:', currentUser.roles);
        
        let userRoles = [];
        if (currentUser.roles) {
            if (Array.isArray(currentUser.roles)) {
                userRoles = [...currentUser.roles];
            } else if (typeof currentUser.roles === 'string') {
                try {
                    userRoles = JSON.parse(currentUser.roles);
                } catch (e) {
                    userRoles = [];
                }
            }
        }
        
        // 기본 role도 확인
        if (currentUser.role) {
            userRoles.push(currentUser.role);
        }
        
        console.log('🔍 userRoles:', userRoles);
        const hasRole = userRoles.includes('admin') || userRoles.includes('finance');
        console.log('🔍 hasAdminOrFinanceRole:', hasRole);
        
        return hasRole;
    };

    // 사용자가 매핑할 수 있는 프로젝트 필터링
    const getAvailableProjects = () => {
        if (hasAdminOrFinanceRole()) {
            // 관리자와 재무담당자는 모든 프로젝트 접근 가능
            return projects;
        }

        // 일반 사용자는 자신이 소속된 프로젝트만 접근 가능
        if (!currentUser) return [];
        
        return projects.filter(project => {
            // PM 또는 기술 리더인 경우
            if (project.pm_id === currentUser.id || project.tech_leader_id === currentUser.id) {
                return true;
            }
            
            // 프로젝트 멤버에서 현재 사용자 확인
            if (project.members && project.members.some(member => 
                member.user_id === currentUser.id || 
                member.userId === currentUser.id ||
                (member.user && member.user.id === currentUser.id)
            )) {
                return true;
            }
            
            return false;
        });
    };

    // 사용자가 매핑할 수 있는 부서 필터링
    const getAvailableDepartments = () => {
        if (hasAdminOrFinanceRole()) {
            // 관리자와 재무담당자는 모든 부서 접근 가능
            return departments;
        }

        // 일반 사용자는 자신이 소속된 부서만 접근 가능
        if (!currentUser || !currentUser.department_id) return [];
        
        return departments.filter(dept => dept.id === currentUser.department_id);
    };

    const fetchInitialData = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            
            if (!token) {
                message.error('로그인이 필요합니다');
                window.location.href = '/login';
                return;
            }
            
            // 기본 API 호출 (모든 사용자가 필요한 데이터)
            const basicPromises = [
                axios.get(`${API_BASE}/corporate-cards/cards`, {
                    headers: { Authorization: `Bearer ${token}` }
                }),
                axios.get(`${API_BASE}/projects`, {
                    headers: { Authorization: `Bearer ${token}` }
                }),
                axios.get(`${API_BASE}/departments`, {
                    headers: { Authorization: `Bearer ${token}` }
                })
            ];
            
            // 사용자 목록은 admin/finance 권한이 있는 경우만 호출
            let usersPromise = null;
            if (currentUser && hasAdminOrFinanceRole()) {
                usersPromise = axios.get(`${API_BASE}/users`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            }
            
            const results = await Promise.all(basicPromises);
            const [cardsRes, projectsRes, departmentsRes] = results;
            
            // 사용자 목록 데이터가 있으면 가져오기
            let usersData = [];
            if (usersPromise) {
                try {
                    const usersRes = await usersPromise;
                    usersData = usersRes.data.users || [];
                } catch (error) {
                    console.error('Users fetch error (ignored for card owners):', error);
                    // 사용자 API 실패는 무시 (카드 오너의 경우)
                }
            }
            
            console.log('API Responses:', {
                cards: cardsRes.data,
                projects: projectsRes.data,
                departments: departmentsRes.data,
                users: usersData
            });
            
            const cardsData = cardsRes.data.cards || [];
            console.log('🔍 카드 데이터:', cardsData);
            
            setCards(cardsData);
            setProjects(projectsRes.data.projects || []);
            setDepartments(departmentsRes.data.departments || []);
            setUsers(usersData);
        } catch (error) {
            console.error('Fetch error:', error);
            
            if (error.response?.status === 401) {
                message.error('인증이 만료되었습니다. 다시 로그인해주세요');
                localStorage.removeItem('token');
                window.location.href = '/login';
                return;
            }
            
            message.error('데이터 로드 실패');
            // 에러 발생 시 빈 배열로 초기화
            setCards([]);
            setProjects([]);
            setDepartments([]);
            setUsers([]);
        } finally {
            setLoading(false);
        }
    };

    // 프로젝트 예산 항목 가져오기
    const fetchProjectBudgetItems = async (projectId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_BASE}/project-budget/project/${projectId}/items`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setProjectBudgetItems(response.data.budget_items || []);
        } catch (error) {
            console.error('프로젝트 예산 항목 조회 실패:', error);
            setProjectBudgetItems([]);
        }
    };

    // 프로젝트 선택 시 처리
    const handleProjectChange = (projectId) => {
        setSelectedProjectId(projectId);
        if (projectId) {
            fetchProjectBudgetItems(projectId);
        } else {
            setProjectBudgetItems([]);
        }
        // 프로젝트 변경 시 기존 선택 초기화
        mappingForm.setFieldsValue({
            budget_category: undefined,
            project_budget_item_id: undefined
        });
        setSelectedBudgetCategory(null);
    };

    // 예산 카테고리 변경 시 처리
    const handleBudgetCategoryChange = (category) => {
        setSelectedBudgetCategory(category);
        // 카테고리 변경 시 예산 항목 초기화
        mappingForm.setFieldsValue({
            project_budget_item_id: undefined
        });
    };

    const fetchTransactions = async () => {
        try {
            const token = localStorage.getItem('token');
            const params = {
                start_date: filters.dateRange[0]?.format('YYYY-MM-DD'),
                end_date: filters.dateRange[1]?.format('YYYY-MM-DD'),
                ...(filters.cardId && { card_id: filters.cardId }),
                ...(filters.mappingStatus && { mapping_status: filters.mappingStatus })
            };
            
            console.log('Fetching transactions with params:', params);
            
            const response = await axios.get(`${API_BASE}/corporate-cards/transactions`, {
                headers: { Authorization: `Bearer ${token}` },
                params
            });
            
            console.log('Transactions API response:', {
                status: response.status,
                data: response.data,
                transactionCount: response.data.data?.transactions?.length
            });
            
            setTransactions(response.data.data?.transactions || []);
        } catch (error) {
            console.error('Transactions fetch error:', error);
            
            if (error.response?.status === 401) {
                message.error('인증이 만료되었습니다. 다시 로그인해주세요');
                localStorage.removeItem('token');
                window.location.href = '/login';
                return;
            }
            
            setTransactions([]);
        }
    };

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem('token');
            const params = {
                start_date: filters.dateRange[0]?.format('YYYY-MM-DD'),
                end_date: filters.dateRange[1]?.format('YYYY-MM-DD')
            };
            
            // 카드 필터가 있으면 추가
            if (filters.cardId) {
                params.card_id = filters.cardId;
            }
            
            console.log('Fetching stats with params:', params);
            
            const response = await axios.get(`${API_BASE}/corporate-cards/transactions/stats`, {
                headers: { Authorization: `Bearer ${token}` },
                params
            });
            
            console.log('Stats API response:', response.data);
            setStats(response.data || {});
        } catch (error) {
            console.error('Stats fetch error:', error);
            
            if (error.response?.status === 401) {
                message.error('인증이 만료되었습니다. 다시 로그인해주세요');
                localStorage.removeItem('token');
                window.location.href = '/login';
                return;
            }
            
            setStats({});
        }
    };

    const handleCardSubmit = async (values) => {
        try {
            const token = localStorage.getItem('token');
            if (editingCard) {
                await axios.put(`${API_BASE}/corporate-cards/cards/${editingCard.id}`, values, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                message.success('카드 정보가 수정되었습니다');
            } else {
                await axios.post(`${API_BASE}/corporate-cards/cards`, values, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                message.success('카드가 등록되었습니다');
            }
            
            setCardModalVisible(false);
            setEditingCard(null);
            cardForm.resetFields();
            console.log('Card operation completed, refreshing data...');
            await fetchInitialData();
        } catch (error) {
            console.error('Card submit error:', error);
            
            if (error.response?.status === 401) {
                message.error('인증이 만료되었습니다. 다시 로그인해주세요');
                localStorage.removeItem('token');
                window.location.href = '/login';
                return;
            }
            
            message.error('카드 등록/수정 실패');
        }
    };

    const handleUpload = async (values) => {
        setUploadLoading(true);
        try {
            const token = localStorage.getItem('token');
            const formData = new FormData();
            
            // 파일 접근 방식 확인 및 수정
            const fileList = values.file;
            if (!fileList || fileList.length === 0) {
                message.error('파일을 선택해주세요');
                return;
            }
            
            const file = fileList[0].originFileObj || fileList[0];
            if (!file) {
                message.error('유효하지 않은 파일입니다');
                return;
            }
            
            console.log('Uploading file:', {
                name: file.name,
                size: file.size,
                type: file.type
            });
            
            formData.append('excel_file', file);
            
            await axios.post(
                `${API_BASE}/corporate-cards/cards/${selectedCard}/upload`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );
            
            message.success('엑셀 파일이 업로드되었습니다');
            setUploadModalVisible(false);
            uploadForm.resetFields();
            fetchTransactions();
            fetchStats();
        } catch (error) {
            console.error('Upload error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status,
                full_error: error
            });
            
            if (error.response?.status === 401) {
                message.error('인증이 만료되었습니다. 다시 로그인해주세요');
                localStorage.removeItem('token');
                window.location.href = '/login';
                return;
            }
            
            const errorMessage = error.response?.data?.message || '파일 업로드 실패';
            message.error(errorMessage);
        } finally {
            setUploadLoading(false);
        }
    };

    const handleMapping = async (values) => {
        try {
            console.log('🔍 매핑 요청 시작:', {
                values,
                selectedTransactions,
                selectedTransactionsLength: selectedTransactions.length
            });

            if (!selectedTransactions || selectedTransactions.length === 0) {
                message.error('매핑할 거래내역을 선택해주세요');
                return;
            }

            const token = localStorage.getItem('token');
            const requestData = {
                transaction_ids: selectedTransactions,
                mapping_type: values.mapping_type,
                ...(values.mapping_type === 'project' && { 
                    project_id: values.project_id,
                    budget_category: values.budget_category,
                    project_budget_item_id: values.project_budget_item_id
                }),
                ...(values.mapping_type === 'department' && { department_id: values.department_id }),
                memo: values.memo
            };

            console.log('🔍 매핑 요청 데이터:', requestData);

            const response = await axios.post(`${API_BASE}/corporate-cards/transactions/map`, requestData, {
                headers: { Authorization: `Bearer ${token}` }
            });

            console.log('✅ 매핑 응답:', response.data);
            
            message.success('매핑이 완료되었습니다');
            setMappingModalVisible(false);
            setSelectedTransactions([]);
            mappingForm.resetFields();
            fetchTransactions();
            fetchStats();
        } catch (error) {
            console.error('❌ 매핑 오류:', error);
            const errorMessage = error.response?.data?.message || '매핑 실패';
            message.error(errorMessage);
        }
    };

    const handleUnmap = async () => {
        try {
            const token = localStorage.getItem('token');
            await axios.post(`${API_BASE}/corporate-cards/transactions/unmap`, {
                transaction_ids: selectedTransactions
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            message.success('매핑이 해제되었습니다');
            setSelectedTransactions([]);
            fetchTransactions();
            fetchStats();
        } catch (error) {
            message.error('매핑 해제 실패');
            console.error('Unmap error:', error);
        }
    };

    const handleDelete = async () => {
        Modal.confirm({
            title: '거래내역 삭제',
            content: `선택한 ${selectedTransactions.length}건의 거래내역을 삭제하시겠습니까?`,
            okText: '삭제',
            okType: 'danger',
            cancelText: '취소',
            onOk: async () => {
                try {
                    const token = localStorage.getItem('token');
                    await axios.delete(`${API_BASE}/corporate-cards/transactions`, {
                        headers: { Authorization: `Bearer ${token}` },
                        data: { transaction_ids: selectedTransactions }
                    });
                    
                    message.success('거래내역이 삭제되었습니다');
                    setSelectedTransactions([]);
                    fetchTransactions();
                    fetchStats();
                } catch (error) {
                    if (handleAuthError(error)) return;
                    
                    const errorMessage = error.response?.data?.message || '거래내역 삭제 실패';
                    message.error(errorMessage);
                    console.error('Delete error:', error);
                }
            }
        });
    };

    const handleSingleMapping = async (values) => {
        try {
            const token = localStorage.getItem('token');
            await axios.post(`${API_BASE}/corporate-cards/transactions/map`, {
                transaction_ids: [currentTransaction.id],
                mapping_type: values.mapping_type,
                ...(values.mapping_type === 'project' && { 
                    project_id: values.project_id,
                    budget_category: values.budget_category,
                    project_budget_item_id: values.project_budget_item_id
                }),
                ...(values.mapping_type === 'department' && { department_id: values.department_id }),
                memo: values.memo
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            message.success('매핑이 완료되었습니다');
            setSingleMappingModalVisible(false);
            setCurrentTransaction(null);
            singleMappingForm.resetFields();
            fetchTransactions();
            fetchStats();
        } catch (error) {
            if (handleAuthError(error)) return;
            
            const errorMessage = error.response?.data?.message || '매핑 실패';
            message.error(errorMessage);
            console.error('Single mapping error:', error);
        }
    };

    const handleSingleUnmap = async (transactionId) => {
        try {
            const token = localStorage.getItem('token');
            await axios.post(`${API_BASE}/corporate-cards/transactions/unmap`, {
                transaction_ids: [transactionId]
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            message.success('매핑이 해제되었습니다');
            fetchTransactions();
            fetchStats();
        } catch (error) {
            if (handleAuthError(error)) return;
            
            const errorMessage = error.response?.data?.message || '매핑 해제 실패';
            message.error(errorMessage);
            console.error('Single unmap error:', error);
        }
    };

    const openSingleMappingModal = (transaction) => {
        setCurrentTransaction(transaction);
        
        // 기존 매핑 정보로 폼 초기화
        const initialValues = {};
        if (transaction.mapping_status === 'project_mapped' && transaction.project) {
            initialValues.mapping_type = 'project';
            initialValues.project_id = transaction.project.id;
        } else if (transaction.mapping_status === 'department_mapped' && transaction.department) {
            initialValues.mapping_type = 'department';
            initialValues.department_id = transaction.department.id;
        }
        if (transaction.memo) {
            initialValues.memo = transaction.memo;
        }
        
        singleMappingForm.setFieldsValue(initialValues);
        setSingleMappingModalVisible(true);
    };

    // 카드 테이블 컬럼
    const getCardColumns = () => {
        const baseColumns = [
        {
            title: '카드사',
            dataIndex: 'card_company',
            key: 'card_company',
            render: (company) => <Tag color="blue">{company}</Tag>
        },
        {
            title: '카드명',
            dataIndex: 'card_name',
            key: 'card_name',
            render: (name, record) => (
                <Space>
                    <CreditCardOutlined />
                    <div>
                        <div><Text strong>{name}</Text></div>
                        <div><Text type="secondary">****{record.card_number_last4}</Text></div>
                    </div>
                </Space>
            )
        },
        {
            title: '카드 소유자',
            dataIndex: 'owner',
            key: 'owner',
            render: (owner) => (
                owner ? (
                    <div>
                        <div><Text strong>{owner.name}</Text></div>
                        <div><Text type="secondary">({owner.employee_id}) {owner.department?.name || '미배정'}</Text></div>
                    </div>
                ) : (
                    <Text type="secondary">미지정</Text>
                )
            )
        },
        {
            title: '카드 타입',
            dataIndex: 'card_type',
            key: 'card_type',
            render: (type) => (
                <Tag color={type === 'domestic' ? 'green' : 'orange'}>
                    {type === 'domestic' ? '국내' : '국외'}
                </Tag>
            )
        },
        {
            title: '상태',
            dataIndex: 'status',
            key: 'status',
            render: (status) => (
                <Tag color={status === 'active' ? 'green' : 'red'}>
                    {status === 'active' ? '활성' : '비활성'}
                </Tag>
            )
        },
        {
            title: '설명',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true
        }
        ];

        // 관리자나 재무 권한이 있으면 모든 액션 표시
        if (hasAdminOrFinanceRole()) {
            baseColumns.push({
                title: '작업',
                key: 'action',
                render: (_, record) => (
                    <Space>
                        <Button 
                            size="small" 
                            icon={<UploadOutlined />}
                            onClick={() => {
                                setSelectedCard(record.id);
                                setSelectedCardInfo(record);
                                setUploadModalVisible(true);
                            }}
                        >
                            업로드
                        </Button>
                        <Button 
                            size="small" 
                            icon={<EditOutlined />}
                            onClick={() => {
                                setEditingCard(record);
                                cardForm.setFieldsValue(record);
                                setCardModalVisible(true);
                            }}
                        />
                    </Space>
                )
            });
        } else if (currentUser) {
            // 카드 소유자는 업로드만 가능
            baseColumns.push({
                title: '작업',
                key: 'action',
                render: (_, record) => {
                    const isOwner = record.owner?.id === currentUser.id;
                    return isOwner ? (
                        <Button 
                            size="small" 
                            icon={<UploadOutlined />}
                            onClick={() => {
                                setSelectedCard(record.id);
                                setSelectedCardInfo(record);
                                setUploadModalVisible(true);
                            }}
                        >
                            업로드
                        </Button>
                    ) : null;
                }
            });
        }

        return baseColumns;
    };

    // 거래내역 테이블 컬럼
    const transactionColumns = [
        {
            title: '거래일시',
            dataIndex: 'transaction_date',
            key: 'transaction_date',
            render: (date) => dayjs(date).format('YYYY-MM-DD HH:mm')
        },
        {
            title: '카드',
            dataIndex: 'card',
            key: 'card',
            render: (card) => (
                <div>
                    <div><Text strong>{card.card_name}</Text></div>
                    <div><Text type="secondary">{card.card_company}</Text></div>
                </div>
            )
        },
        {
            title: '승인번호',
            dataIndex: 'approval_number',
            key: 'approval_number',
            render: (number) => <Text code>{number}</Text>
        },
        {
            title: '가맹점',
            dataIndex: 'merchant_name',
            key: 'merchant_name',
            ellipsis: true
        },
        {
            title: '금액',
            dataIndex: 'amount',
            key: 'amount',
            align: 'right',
            render: (amount) => (
                <Text strong style={{ color: '#ff4d4f' }}>
                    {parseInt(amount).toLocaleString()}원
                </Text>
            )
        },
        {
            title: '매핑상태',
            dataIndex: 'mapping_status',
            key: 'mapping_status',
            render: (status, record) => {
                const statusConfig = {
                    unmapped: { color: 'default', text: '미매핑' },
                    project_mapped: { color: 'blue', text: '프로젝트' },
                    department_mapped: { color: 'green', text: '부서' }
                };
                const config = statusConfig[status];
                return (
                    <div>
                        <Space direction="vertical" size="small" style={{ display: 'flex' }}>
                            <Space>
                                <Tag 
                                    color={config.color} 
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => openSingleMappingModal(record)}
                                >
                                    {config.text}
                                </Tag>
                                {status !== 'unmapped' && (
                                    <Button
                                        type="link"
                                        size="small"
                                        danger
                                        icon={<CloseOutlined />}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleSingleUnmap(record.id);
                                        }}
                                        style={{ padding: 0, minWidth: 'auto' }}
                                    />
                                )}
                            </Space>
                            {status === 'project_mapped' && record.project && (
                                <div>
                                    <div><Text type="secondary" style={{ fontSize: 12 }}>{record.project.name}</Text></div>
                                    {record.budget_category && (
                                        <div>
                                            <Tag size="small" color="orange">
                                                {getBudgetCategoryName(record.budget_category)}
                                            </Tag>
                                        </div>
                                    )}
                                </div>
                            )}
                            {status === 'department_mapped' && record.department && (
                                <div>
                                    <div><Text type="secondary" style={{ fontSize: 12 }}>{record.department.name}</Text></div>
                                    {record.budget_category && (
                                        <div>
                                            <Tag size="small" color="orange">
                                                {getBudgetCategoryName(record.budget_category)}
                                            </Tag>
                                        </div>
                                    )}
                                </div>
                            )}
                        </Space>
                    </div>
                );
            }
        },
        {
            title: '메모',
            dataIndex: 'memo',
            key: 'memo',
            ellipsis: true
        }
    ];

    // 테이블 행 선택
    const rowSelection = {
        selectedRowKeys: selectedTransactions,
        onChange: (selectedRowKeys) => {
            setSelectedTransactions(selectedRowKeys);
        }
    };

    // 접근 권한 체크
    const hasAccess = () => {
        return hasAdminOrFinanceRole() || (cards && cards.length > 0);
    };

    // 데이터 로딩 중이면 로딩 표시
    if (!currentUser || loading) {
        return (
            <div style={{ padding: 24, textAlign: 'center' }}>
                <Title level={2}>💳 법인카드 관리</Title>
                <div>데이터를 불러오는 중...</div>
            </div>
        );
    }

    // 권한이 없는 경우 안내 메시지 표시 (관리자/재무가 아니고 소유 카드가 없는 경우)
    const isAdminOrFinance = hasAdminOrFinanceRole();
    const hasOwnedCards = cards && cards.length > 0;
    
    console.log('🔍 권한 체크:', {
        isAdminOrFinance,
        hasOwnedCards,
        cardsLength: cards?.length
    });
    
    if (!isAdminOrFinance && !hasOwnedCards) {
        return (
            <div style={{ padding: 24, textAlign: 'center' }}>
                <Title level={2}>💳 법인카드 관리</Title>
                <Alert
                    message="접근 권한 없음"
                    description={
                        <div>
                            <p>법인카드 관리 권한이 없거나 소유중인 카드가 없습니다.</p>
                            <p>현재 상태: 관리자/재무 권한: {isAdminOrFinance ? 'O' : 'X'}, 소유 카드: {hasOwnedCards ? `${cards.length}개` : '없음'}</p>
                            <p>관리자에게 문의하세요.</p>
                        </div>
                    }
                    type="info"
                    showIcon
                    style={{ maxWidth: 600, margin: '0 auto' }}
                />
            </div>
        );
    }

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <Title level={2}>💳 법인카드 관리</Title>
                {hasAdminOrFinanceRole() && (
                    <Button 
                        type="primary" 
                        icon={<PlusOutlined />}
                        onClick={() => {
                            setEditingCard(null);
                            cardForm.resetFields();
                            setCardModalVisible(true);
                        }}
                    >
                        카드 등록
                    </Button>
                )}
            </div>

            {/* 통계 카드 */}
            <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="총 사용금액"
                            value={stats.total_amount || 0}
                            formatter={(value) => `${parseInt(value).toLocaleString()}원`}
                            valueStyle={{ color: '#ff4d4f' }}
                            prefix={<CreditCardOutlined />}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="총 거래건수"
                            value={stats.total_count || 0}
                            suffix="건"
                            prefix={<FileExcelOutlined />}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="매핑완료"
                            value={stats.mapped_count || 0}
                            suffix={`/ ${stats.total_count || 0}건`}
                            prefix={<CheckOutlined />}
                            valueStyle={{ color: '#52c41a' }}
                        />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <Statistic
                            title="미매핑"
                            value={stats.unmapped_count || 0}
                            suffix="건"
                            prefix={<CloseOutlined />}
                            valueStyle={{ color: '#faad14' }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* 법인카드 목록 */}
            <Card title="💳 법인카드 목록" style={{ marginBottom: 24 }}>
                <Table
                    columns={getCardColumns()}
                    dataSource={cards}
                    rowKey="id"
                    loading={loading}
                    size="small"
                    pagination={false}
                />
            </Card>

            {/* 필터 및 액션 */}
            <Card style={{ marginBottom: 16 }}>
                <Row gutter={16} align="middle">
                    <Col span={8}>
                        <Space>
                            <Text>기간:</Text>
                            <RangePicker
                                value={filters.dateRange}
                                onChange={(dates) => setFilters(prev => ({ ...prev, dateRange: dates }))}
                                format="YYYY-MM-DD"
                            />
                        </Space>
                    </Col>
                    <Col span={6}>
                        <Space>
                            <Text>카드:</Text>
                            <Select
                                value={filters.cardId}
                                onChange={(value) => setFilters(prev => ({ ...prev, cardId: value }))}
                                style={{ width: 180 }}
                                allowClear
                                placeholder="전체"
                            >
                                {cards?.map(card => (
                                    <Option key={card.id} value={card.id}>
                                        {card.card_name}
                                    </Option>
                                ))}
                            </Select>
                        </Space>
                    </Col>
                    <Col span={6}>
                        <Space>
                            <Text>매핑상태:</Text>
                            <Select
                                value={filters.mappingStatus}
                                onChange={(value) => setFilters(prev => ({ ...prev, mappingStatus: value }))}
                                style={{ width: 120 }}
                                allowClear
                                placeholder="전체"
                            >
                                <Option value="unmapped">미매핑</Option>
                                <Option value="project_mapped">프로젝트</Option>
                                <Option value="department_mapped">부서</Option>
                            </Select>
                        </Space>
                    </Col>
                    <Col span={4}>
                        <Space>
                            <Button
                                type="primary"
                                icon={<ProjectOutlined />}
                                disabled={selectedTransactions.length === 0}
                                onClick={() => setMappingModalVisible(true)}
                            >
                                매핑
                            </Button>
                            <Button
                                danger
                                disabled={selectedTransactions.length === 0}
                                onClick={handleUnmap}
                            >
                                해제
                            </Button>
                            <Button
                                danger
                                icon={<DeleteOutlined />}
                                disabled={selectedTransactions.length === 0}
                                onClick={handleDelete}
                            >
                                삭제
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </Card>

            {/* 거래내역 목록 */}
            <Card title="📋 거래내역 목록">
                {selectedTransactions.length > 0 && (
                    <Alert
                        message={`${selectedTransactions.length}건이 선택되었습니다.`}
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                )}
                <Table
                    columns={transactionColumns}
                    dataSource={transactions}
                    rowKey="id"
                    loading={loading}
                    size="small"
                    rowSelection={rowSelection}
                    scroll={{ x: 1200 }}
                    pagination={{
                        pageSize: 20,
                        showTotal: (total) => `총 ${total}건`,
                        showSizeChanger: true
                    }}
                />
            </Card>

            {/* 카드 등록/수정 모달 */}
            <Modal
                title={editingCard ? "카드 수정" : "카드 등록"}
                open={cardModalVisible}
                onCancel={() => {
                    setCardModalVisible(false);
                    setEditingCard(null);
                    cardForm.resetFields();
                }}
                footer={null}
            >
                <Form
                    form={cardForm}
                    layout="vertical"
                    onFinish={handleCardSubmit}
                >
                    <Form.Item
                        name="card_company"
                        label="카드사"
                        rules={[{ required: true, message: '카드사를 입력하세요' }]}
                    >
                        <Select placeholder="카드사 선택">
                            <Option value="삼성">삼성</Option>
                            <Option value="신한">신한</Option>
                            <Option value="KB">KB</Option>
                            <Option value="현대">현대</Option>
                            <Option value="농협">농협</Option>
                            <Option value="롯데">롯데</Option>
                            <Option value="하나">하나</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="card_number_last4"
                        label="카드번호 뒷자리"
                        rules={[
                            { required: true, message: '카드번호 뒷자리를 입력하세요' },
                            { len: 4, message: '4자리 숫자를 입력하세요' }
                        ]}
                    >
                        <Input maxLength={4} placeholder="1234" />
                    </Form.Item>
                    <Form.Item
                        name="card_name"
                        label="카드명"
                        rules={[{ required: true, message: '카드명을 입력하세요' }]}
                    >
                        <Input placeholder="카드명 입력" />
                    </Form.Item>
                    <Form.Item
                        name="owner_id"
                        label="카드 소유자"
                        rules={[{ required: true, message: '카드 소유자를 선택하세요' }]}
                    >
                        <Select
                            placeholder="카드 소유자 선택"
                            showSearch
                            optionFilterProp="children"
                            filterOption={(input, option) =>
                                option.children.toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {users.map(user => (
                                <Option key={user.id} value={user.id}>
                                    {user.name} ({user.employee_id}) - {user.department?.name || '미배정'}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="card_type"
                        label="카드 타입"
                        initialValue="domestic"
                    >
                        <Select>
                            <Option value="domestic">국내</Option>
                            <Option value="international">국외</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="status"
                        label="상태"
                        initialValue="active"
                    >
                        <Select>
                            <Option value="active">활성</Option>
                            <Option value="inactive">비활성</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="description"
                        label="설명"
                    >
                        <Input.TextArea rows={3} placeholder="카드 설명" />
                    </Form.Item>
                    <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                        <Space>
                            <Button onClick={() => setCardModalVisible(false)}>취소</Button>
                            <Button type="primary" htmlType="submit">
                                {editingCard ? '수정' : '등록'}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {/* 엑셀 업로드 모달 */}
            <Modal
                title="엑셀 파일 업로드"
                open={uploadModalVisible}
                onCancel={() => {
                    setUploadModalVisible(false);
                    setUploadLoading(false);
                    uploadForm.resetFields();
                }}
                footer={null}
                confirmLoading={uploadLoading}
            >
                {/* 선택된 카드 정보 표시 */}
                {selectedCardInfo && (
                    <Card 
                        size="small" 
                        style={{ marginBottom: 16, backgroundColor: '#f5f5f5' }}
                        title={<span><CreditCardOutlined style={{ marginRight: 8 }} />선택된 카드 정보</span>}
                    >
                        <Row gutter={16}>
                            <Col span={12}>
                                <div>
                                    <Text type="secondary">카드사: </Text>
                                    <Tag color="blue">{selectedCardInfo.card_company}</Tag>
                                </div>
                            </Col>
                            <Col span={12}>
                                <div>
                                    <Text type="secondary">카드 타입: </Text>
                                    <Tag color={selectedCardInfo.card_type === 'domestic' ? 'green' : 'orange'}>
                                        {selectedCardInfo.card_type === 'domestic' ? '국내' : '국외'}
                                    </Tag>
                                </div>
                            </Col>
                            <Col span={24} style={{ marginTop: 8 }}>
                                <div>
                                    <Text type="secondary">카드명: </Text>
                                    <Text strong>{selectedCardInfo.card_name}</Text>
                                    <Text type="secondary" style={{ marginLeft: 16 }}>
                                        ****{selectedCardInfo.card_number_last4}
                                    </Text>
                                </div>
                            </Col>
                        </Row>
                    </Card>
                )}
                
                <Form
                    form={uploadForm}
                    layout="vertical"
                    onFinish={handleUpload}
                >
                    <Form.Item
                        name="file"
                        label="엑셀 파일"
                        rules={[{ required: true, message: '엑셀 파일을 선택하세요' }]}
                        valuePropName="fileList"
                        getValueFromEvent={(e) => {
                            if (Array.isArray(e)) {
                                return e;
                            }
                            return e && e.fileList;
                        }}
                    >
                        <Upload
                            beforeUpload={() => false}
                            maxCount={1}
                            accept=".xlsx,.xls,.xlsm"
                            listType="text"
                        >
                            <Button icon={<UploadOutlined />}>파일 선택</Button>
                        </Upload>
                    </Form.Item>
                    <Alert
                        message="업로드 안내"
                        description="지원 형식: .xlsx, .xls, .xlsm (최대 10MB)"
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                    <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                        <Space>
                            <Button 
                                onClick={() => setUploadModalVisible(false)}
                                disabled={uploadLoading}
                            >
                                취소
                            </Button>
                            <Button 
                                type="primary" 
                                htmlType="submit" 
                                loading={uploadLoading}
                                disabled={uploadLoading}
                                icon={uploadLoading ? null : <UploadOutlined />}
                            >
                                {uploadLoading ? '업로드 중...' : '업로드'}
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {/* 매핑 모달 */}
            <Modal
                title="거래내역 매핑"
                open={mappingModalVisible}
                onCancel={() => {
                    setMappingModalVisible(false);
                    mappingForm.resetFields();
                }}
                footer={null}
            >
                <Form
                    form={mappingForm}
                    layout="vertical"
                    onFinish={handleMapping}
                >
                    <Form.Item
                        name="mapping_type"
                        label="매핑 타입"
                        rules={[{ required: true, message: '매핑 타입을 선택하세요' }]}
                    >
                        <Select placeholder="매핑 타입 선택">
                            <Option value="project">프로젝트</Option>
                            <Option value="department">부서</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, currentValues) =>
                            prevValues.mapping_type !== currentValues.mapping_type
                        }
                    >
                        {({ getFieldValue }) => {
                            const mappingType = getFieldValue('mapping_type');
                            if (mappingType === 'project') {
                                const availableProjects = getAvailableProjects();
                                return (
                                    <Form.Item
                                        name="project_id"
                                        label="프로젝트"
                                        rules={[{ required: true, message: '프로젝트를 선택하세요' }]}
                                    >
                                        <Select 
                                            placeholder="프로젝트 선택" 
                                            showSearch
                                            optionFilterProp="children"
                                            filterOption={(input, option) => {
                                                if (!option || !option.children) return false;
                                                const text = typeof option.children === 'string' ? option.children : option.children.join('');
                                                return text.toLowerCase().includes(input.toLowerCase());
                                            }}
                                            onChange={handleProjectChange}
                                        >
                                            {availableProjects?.map(project => (
                                                <Option key={project.id} value={project.id}>
                                                    [{project.code}] {project.name}
                                                </Option>
                                            ))}
                                        </Select>
                                        {availableProjects?.length === 0 && (
                                            <div style={{ marginTop: 8, color: '#666', fontSize: 12 }}>
                                                💡 소속된 프로젝트가 없습니다. 프로젝트 참여 후 매핑이 가능합니다.
                                            </div>
                                        )}
                                    </Form.Item>
                                );
                            }
                            if (mappingType === 'department') {
                                const availableDepartments = getAvailableDepartments();
                                return (
                                    <Form.Item
                                        name="department_id"
                                        label="부서"
                                        rules={[{ required: true, message: '부서를 선택하세요' }]}
                                    >
                                        <Select placeholder="부서 선택">
                                            {availableDepartments?.map(dept => (
                                                <Option key={dept.id} value={dept.id}>
                                                    {dept.name}
                                                </Option>
                                            ))}
                                        </Select>
                                        {availableDepartments?.length === 0 && (
                                            <div style={{ marginTop: 8, color: '#666', fontSize: 12 }}>
                                                💡 소속 부서 정보가 없습니다. 관리자에게 문의하세요.
                                            </div>
                                        )}
                                    </Form.Item>
                                );
                            }
                            return null;
                        }}
                    </Form.Item>
                    
                    {/* 예산 카테고리 (프로젝트 매핑 시에만 표시) */}
                    <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, currentValues) =>
                            prevValues.mapping_type !== currentValues.mapping_type
                        }
                    >
                        {({ getFieldValue }) => {
                            const mappingType = getFieldValue('mapping_type');
                            if (mappingType === 'project') {
                                return (
                                    <Form.Item
                                        name="budget_category"
                                        label="예산 카테고리"
                                        rules={[{ required: true, message: '예산 카테고리를 선택하세요' }]}
                                    >
                                        <Select 
                                            placeholder="예산 카테고리 선택"
                                            onChange={handleBudgetCategoryChange}
                                        >
                                            <Option value="outsourcing">외주비</Option>
                                            <Option value="material">자재비</Option>
                                            <Option value="expense">경비</Option>
                                            <Option value="other">기타</Option>
                                        </Select>
                                    </Form.Item>
                                );
                            }
                            return null;
                        }}
                    </Form.Item>

                    {/* 예산 항목 (프로젝트 매핑 및 카테고리 선택 시에만 표시) */}
                    <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, currentValues) =>
                            prevValues.mapping_type !== currentValues.mapping_type ||
                            prevValues.budget_category !== currentValues.budget_category ||
                            prevValues.project_id !== currentValues.project_id
                        }
                    >
                        {({ getFieldValue }) => {
                            const mappingType = getFieldValue('mapping_type');
                            const budgetCategory = getFieldValue('budget_category');
                            const projectId = getFieldValue('project_id');

                            if (mappingType === 'project' && budgetCategory && projectId) {
                                const filteredItems = projectBudgetItems.filter(item => 
                                    item.category === budgetCategory
                                );

                                return (
                                    <Form.Item
                                        name="project_budget_item_id"
                                        label="예산 항목"
                                        extra="프로젝트 예산편성에서 등록된 항목 중 선택 (선택사항)"
                                    >
                                        <Select 
                                            placeholder="예산 항목 선택 (선택사항)"
                                            allowClear
                                            showSearch
                                            optionFilterProp="label"
                                        >
                                            {filteredItems.map(item => (
                                                <Option key={item.id} value={item.id}>
                                                    {item.vendor_name 
                                                        ? `${item.vendor_name} - ${item.description}`
                                                        : item.description
                                                    }
                                                </Option>
                                            ))}
                                        </Select>
                                        {filteredItems.length === 0 && budgetCategory && (
                                            <div style={{ marginTop: 8, color: '#666', fontSize: 12 }}>
                                                💡 선택된 카테고리에 등록된 예산 항목이 없습니다.
                                            </div>
                                        )}
                                    </Form.Item>
                                );
                            }
                            return null;
                        }}
                    </Form.Item>
                    
                    <Form.Item
                        name="memo"
                        label="메모"
                    >
                        <Input.TextArea rows={3} placeholder="매핑 관련 메모를 입력하세요 (선택사항)" />
                    </Form.Item>
                    
                    <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                        <Space>
                            <Button onClick={() => setMappingModalVisible(false)}>취소</Button>
                            <Button type="primary" htmlType="submit">매핑</Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {/* 개별 거래내역 매핑 모달 */}
            <Modal
                title="거래내역 매핑"
                open={singleMappingModalVisible}
                onCancel={() => {
                    setSingleMappingModalVisible(false);
                    setCurrentTransaction(null);
                    singleMappingForm.resetFields();
                }}
                footer={null}
            >
                {currentTransaction && (
                    <div style={{ marginBottom: 16, padding: 16, backgroundColor: '#f5f5f5', borderRadius: 6 }}>
                        <Space direction="vertical" size="small">
                            <div><Text strong>거래일시:</Text> {dayjs(currentTransaction.transaction_date).format('YYYY-MM-DD HH:mm')}</div>
                            <div><Text strong>가맹점:</Text> {currentTransaction.merchant_name}</div>
                            <div><Text strong>금액:</Text> <Text style={{ color: '#ff4d4f' }}>{parseInt(currentTransaction.amount).toLocaleString()}원</Text></div>
                        </Space>
                    </div>
                )}
                <Form
                    form={singleMappingForm}
                    layout="vertical"
                    onFinish={handleSingleMapping}
                >
                    <Form.Item
                        name="mapping_type"
                        label="매핑 타입"
                        rules={[{ required: true, message: '매핑 타입을 선택하세요' }]}
                    >
                        <Select placeholder="매핑 타입 선택">
                            <Option value="project">프로젝트</Option>
                            <Option value="department">부서</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, currentValues) =>
                            prevValues.mapping_type !== currentValues.mapping_type
                        }
                    >
                        {({ getFieldValue }) => {
                            const mappingType = getFieldValue('mapping_type');
                            if (mappingType === 'project') {
                                const availableProjects = getAvailableProjects();
                                return (
                                    <Form.Item
                                        name="project_id"
                                        label="프로젝트"
                                        rules={[{ required: true, message: '프로젝트를 선택하세요' }]}
                                    >
                                        <Select 
                                            placeholder="프로젝트 선택" 
                                            showSearch
                                            optionFilterProp="children"
                                            filterOption={(input, option) => {
                                                if (!option || !option.children) return false;
                                                const text = typeof option.children === 'string' ? option.children : option.children.join('');
                                                return text.toLowerCase().includes(input.toLowerCase());
                                            }}
                                            onChange={handleProjectChange}
                                        >
                                            {availableProjects?.map(project => (
                                                <Option key={project.id} value={project.id}>
                                                    [{project.code}] {project.name}
                                                </Option>
                                            ))}
                                        </Select>
                                        {availableProjects?.length === 0 && (
                                            <div style={{ marginTop: 8, color: '#666', fontSize: 12 }}>
                                                💡 소속된 프로젝트가 없습니다. 프로젝트 참여 후 매핑이 가능합니다.
                                            </div>
                                        )}
                                    </Form.Item>
                                );
                            }
                            if (mappingType === 'department') {
                                const availableDepartments = getAvailableDepartments();
                                return (
                                    <Form.Item
                                        name="department_id"
                                        label="부서"
                                        rules={[{ required: true, message: '부서를 선택하세요' }]}
                                    >
                                        <Select placeholder="부서 선택">
                                            {availableDepartments?.map(dept => (
                                                <Option key={dept.id} value={dept.id}>
                                                    {dept.name}
                                                </Option>
                                            ))}
                                        </Select>
                                        {availableDepartments?.length === 0 && (
                                            <div style={{ marginTop: 8, color: '#666', fontSize: 12 }}>
                                                💡 소속 부서 정보가 없습니다. 관리자에게 문의하세요.
                                            </div>
                                        )}
                                    </Form.Item>
                                );
                            }
                            return null;
                        }}
                    </Form.Item>
                    
                    {/* 예산 카테고리 (프로젝트 매핑 시에만 표시) */}
                    <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, currentValues) =>
                            prevValues.mapping_type !== currentValues.mapping_type
                        }
                    >
                        {({ getFieldValue }) => {
                            const mappingType = getFieldValue('mapping_type');
                            if (mappingType === 'project') {
                                return (
                                    <Form.Item
                                        name="budget_category"
                                        label="예산 카테고리"
                                        rules={[{ required: true, message: '예산 카테고리를 선택하세요' }]}
                                    >
                                        <Select 
                                            placeholder="예산 카테고리 선택"
                                            onChange={handleBudgetCategoryChange}
                                        >
                                            <Option value="outsourcing">외주비</Option>
                                            <Option value="material">자재비</Option>
                                            <Option value="expense">경비</Option>
                                            <Option value="other">기타</Option>
                                        </Select>
                                    </Form.Item>
                                );
                            }
                            return null;
                        }}
                    </Form.Item>

                    {/* 예산 항목 (프로젝트 매핑 및 카테고리 선택 시에만 표시) */}
                    <Form.Item
                        noStyle
                        shouldUpdate={(prevValues, currentValues) =>
                            prevValues.mapping_type !== currentValues.mapping_type ||
                            prevValues.budget_category !== currentValues.budget_category ||
                            prevValues.project_id !== currentValues.project_id
                        }
                    >
                        {({ getFieldValue }) => {
                            const mappingType = getFieldValue('mapping_type');
                            const budgetCategory = getFieldValue('budget_category');
                            const projectId = getFieldValue('project_id');

                            if (mappingType === 'project' && budgetCategory && projectId) {
                                const filteredItems = projectBudgetItems.filter(item => 
                                    item.category === budgetCategory
                                );

                                return (
                                    <Form.Item
                                        name="project_budget_item_id"
                                        label="예산 항목"
                                        extra="프로젝트 예산편성에서 등록된 항목 중 선택 (선택사항)"
                                    >
                                        <Select 
                                            placeholder="예산 항목 선택 (선택사항)"
                                            allowClear
                                            showSearch
                                            optionFilterProp="label"
                                        >
                                            {filteredItems.map(item => (
                                                <Option key={item.id} value={item.id}>
                                                    {item.vendor_name 
                                                        ? `${item.vendor_name} - ${item.description}`
                                                        : item.description
                                                    }
                                                </Option>
                                            ))}
                                        </Select>
                                        {filteredItems.length === 0 && budgetCategory && (
                                            <div style={{ marginTop: 8, color: '#666', fontSize: 12 }}>
                                                💡 선택된 카테고리에 등록된 예산 항목이 없습니다.
                                            </div>
                                        )}
                                    </Form.Item>
                                );
                            }
                            return null;
                        }}
                    </Form.Item>
                    
                    <Form.Item
                        name="memo"
                        label="메모"
                    >
                        <Input.TextArea rows={3} placeholder="매핑 관련 메모 (선택사항)" />
                    </Form.Item>
                    <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                        <Space>
                            <Button onClick={() => setSingleMappingModalVisible(false)}>취소</Button>
                            <Button type="primary" htmlType="submit">매핑</Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default CorporateCardPage;